import { createSlice } from '@reduxjs/toolkit';

import { clearIdentityHint } from '../services/identityHint';
import {
	markSessionEnded,
	markSessionEstablished,
} from '../services/sessionPresence';

import type { User } from '../api/types';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface AuthState {
	user: Partial<User> | null;
	accessToken: string | null;
	refreshToken: string | null;
	isAuthenticated: boolean;
	isRefreshing: boolean;
	// Ephemeral notice queued by the API layer when a session ends. `null` =
	// nothing pending, `''` = "session ended silently, navigate but don't
	// toast" (user was never logged in), non-empty string = toast that message
	// on the next auth-screen mount. Consumed once by AuthRouterObserver →
	// passed to the auth screen via router location.state, then cleared via
	// `consumeNotice`. NOT persisted; no localStorage round-trip.
	pendingNotice: string | null;
}

const initialState: AuthState = {
	accessToken: null,
	refreshToken: null,
	isAuthenticated: false,
	isRefreshing: false,
	user: null,
	pendingNotice: null,
};

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		setAccessToken: (state, action: PayloadAction<string | null>) => {
			state.accessToken = action.payload;
			state.isAuthenticated = !!action.payload;
			if (action.payload) markSessionEstablished();
			else markSessionEnded();
		},
		setRefreshToken: (state, action: PayloadAction<string | null>) => {
			state.refreshToken = action.payload;
		},
		setIsRefreshing: (state, action: PayloadAction<boolean>) => {
			state.isRefreshing = action.payload;
		},
		clearTokens: (state) => {
			state.accessToken = null;
			state.refreshToken = null;
			state.isAuthenticated = false;
			state.user = null;
			state.isRefreshing = false;
			state.pendingNotice = null;
			markSessionEnded();
			// Explicit logout: user is done with this browser. Drop the
			// welcome-back chooser hint too (don't show their identity on
			// next visit). NOT cleared by `sessionExpired` — the chooser is
			// exactly what we want for "session timed out, come back later."
			clearIdentityHint();
		},
		// Dispatched by the API layer (base.ts) when a 401 + refresh fail proves
		// the session is gone. Replaces the old localStorage + window.location
		// round-trip: instead of persisting a message and force-reloading the
		// SPA, we queue an ephemeral notice and let the router observer soft-
		// navigate. Empty `notice` means "no message" (visitor was never
		// logged in — see hasSessionHint gating in base.ts).
		sessionExpired: (state, action: PayloadAction<{ notice: string }>) => {
			state.accessToken = null;
			state.refreshToken = null;
			state.user = null;
			state.isAuthenticated = false;
			state.isRefreshing = false;
			state.pendingNotice = action.payload.notice;
			markSessionEnded();
		},
		consumeNotice: (state) => {
			state.pendingNotice = null;
		},
		setCredentials: (
			state,
			action: PayloadAction<{
				user?: Partial<User>;
				accessToken?: string;
				refreshToken?: string;
			}>,
		) => {
			const { user, accessToken, refreshToken } = action.payload;

			if (user) {
				state.user = { ...state.user, ...user };
				state.isAuthenticated = true;
				markSessionEstablished();
			}
			if (accessToken !== undefined) {
				state.accessToken = accessToken;
				state.isAuthenticated = !!accessToken;
				if (accessToken) markSessionEstablished();
				else markSessionEnded();
			}
			if (refreshToken !== undefined) {
				state.refreshToken = refreshToken;
			}
		},
		logout: (state) => {
			state.user = null;
			state.accessToken = null;
			state.refreshToken = null;
			state.isAuthenticated = false;
			markSessionEnded();
			clearIdentityHint();
		},
	},
});

export const {
	setAccessToken,
	setRefreshToken,
	setIsRefreshing,
	clearTokens,
	setCredentials,
	logout,
	sessionExpired,
	consumeNotice,
} = authSlice.actions;
export default authSlice.reducer;
